var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useConnectionContext } from "@saberhq/use-solana";
import { PublicKey } from "@solana/web3.js";
import DataLoader from "dataloader";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AccountsEmitter } from "./emitter";
import { getMultipleAccounts } from "./fetchers";
import { fetchKeysUsingLoader } from "./fetchKeysUsingLoader";
/**
 * Gets the cache key associated with the given pubkey.
 * @param pubkey
 * @returns
 */
export const getCacheKeyOfPublicKey = (pubkey) => pubkey.toBuffer().toString("base64");
const newState = () => ({
    accountsCache: new Map(),
    emitter: new AccountsEmitter(),
    subscribedAccounts: new Map(),
});
export const useAccountsInternal = ({ batchDurationMs = 500, refreshIntervalMs = 60000, onAccountLoadError, onGetMultipleAccountsError, }) => {
    const { network, connection } = useConnectionContext();
    // Cache of accounts
    const [{ accountsCache, emitter, subscribedAccounts }, setState] = useState(newState());
    useEffect(() => {
        // clear accounts cache and subscriptions whenever the network changes
        accountsCache.clear();
        subscribedAccounts.clear();
        emitter.raiseCacheCleared();
        setState(newState());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [network]);
    const accountLoader = useMemo(() => new DataLoader((keys) => __awaiter(void 0, void 0, void 0, function* () {
        const result = yield getMultipleAccounts(connection, keys, onGetMultipleAccountsError, "recent");
        result.array.forEach((info, i) => {
            const addr = keys[i];
            if (addr && !(info instanceof Error)) {
                accountsCache.set(getCacheKeyOfPublicKey(addr), info);
                emitter.raiseCacheUpdated(addr, true);
            }
        });
        return result.array;
    }), {
        // aggregate all requests over 500ms
        batchScheduleFn: (callback) => setTimeout(callback, batchDurationMs),
        cacheKeyFn: getCacheKeyOfPublicKey,
    }), [
        accountsCache,
        batchDurationMs,
        connection,
        emitter,
        onGetMultipleAccountsError,
    ]);
    const fetchKeys = useCallback((keys) => __awaiter(void 0, void 0, void 0, function* () {
        return yield fetchKeysUsingLoader(accountLoader, keys, onAccountLoadError);
    }), [accountLoader, onAccountLoadError]);
    const onCache = useMemo(() => emitter.onCache.bind(emitter), [emitter]);
    const refetch = useCallback((key) => __awaiter(void 0, void 0, void 0, function* () {
        const result = yield accountLoader.clear(key).load(key);
        return result;
    }), [accountLoader]);
    const getCached = useCallback((key) => {
        // null: account not found on blockchain
        // undefined: cache miss (not yet fetched)
        return accountsCache.get(getCacheKeyOfPublicKey(key));
    }, [accountsCache]);
    const subscribe = useCallback((key) => {
        const keyStr = getCacheKeyOfPublicKey(key);
        const amount = subscribedAccounts.get(keyStr);
        if (amount === undefined || amount === 0) {
            subscribedAccounts.set(keyStr, 1);
        }
        else {
            subscribedAccounts.set(keyStr, amount + 1);
        }
        return () => {
            const currentAmount = subscribedAccounts.get(keyStr);
            if ((currentAmount !== null && currentAmount !== void 0 ? currentAmount : 0) > 1) {
                subscribedAccounts.set(keyStr, (currentAmount !== null && currentAmount !== void 0 ? currentAmount : 0) - 1);
            }
            else {
                subscribedAccounts.delete(keyStr);
            }
            return Promise.resolve();
        };
    }, [subscribedAccounts]);
    const refetchAllSubscriptions = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        return yield Promise.all([...subscribedAccounts.keys()].map((keyStr) => __awaiter(void 0, void 0, void 0, function* () {
            const key = new PublicKey(Buffer.from(keyStr, "base64"));
            yield refetch(key);
        })));
    }), [refetch, subscribedAccounts]);
    useEffect(() => {
        const interval = setInterval(() => {
            void refetchAllSubscriptions();
        }, refreshIntervalMs);
        return () => clearInterval(interval);
    }, [refetchAllSubscriptions, refreshIntervalMs]);
    return {
        loader: accountLoader,
        getCached,
        refetch,
        onCache,
        fetchKeys,
        subscribe,
    };
};
//# sourceMappingURL=useAccountsInternal.js.map